import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Slider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CropIcon from '@mui/icons-material/Crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import EventsApi from '../../../api/eventsApi/eventsApi';
import Cropper, { MediaSize } from 'react-easy-crop';
import {
  COVER_EXPORT_WIDTH,
  COVER_EXPORT_HEIGHT,
  COVER_EXPORT_ASPECT_RATIO,
  COVER_PREVIEW_MAX_WIDTH_PX,
} from '../../reports/coverPhotoSpec';

interface CoverPhotoModalProps {
  open: boolean;
  onClose: () => void;
  pinId: string;
  userId: string;
  onPhotoUploaded?: () => void;
}

const CoverPhotoModal: React.FC<CoverPhotoModalProps> = ({
  open,
  onClose,
  pinId,
  userId,
  onPhotoUploaded
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [existingCoverPhoto, setExistingCoverPhoto] = useState<string | null>(null);
  const [existingCoverPhotoId, setExistingCoverPhotoId] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const [cropViewportSize, setCropViewportSize] = useState<{ width: number; height: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropFrameRef = useRef<HTMLDivElement>(null);
  const CANVAS_WIDTH = COVER_EXPORT_WIDTH;
  const CANVAS_HEIGHT = COVER_EXPORT_HEIGHT;
  const ASPECT_RATIO = CANVAS_WIDTH / CANVAS_HEIGHT;
  const MIN_ZOOM = 0.12;
  /** Küçük görseller çerçeveyi doldurabilsin diye üst sınır yüksek */
  const MAX_ZOOM = 12;
  const ZOOM_STEP = 0.04;
  const CROP_NUDGE_PX = 14;
  const BRAND = '#0D3D4A';
  const BRAND_SOFT = 'rgba(13, 61, 74, 0.1)';
  const BRAND_BORDER = 'rgba(13, 61, 74, 0.35)';

  const iconControlSx = {
    color: BRAND,
    bgcolor: BRAND_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    '&:hover': { bgcolor: 'rgba(13, 61, 74, 0.16)' },
    '& .MuiSvgIcon-root': { color: BRAND, fontSize: '1.4rem' },
  } as const;

  /** Tema MuiButtonBase root backgroundColor:#000 — outlined butonları açık zemin + net yazı */
  const textBtnOutlinedSx = {
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: BRAND,
    color: BRAND,
    fontWeight: 600,
    '& .MuiButton-startIcon': { color: BRAND },
    '& .MuiButton-startIcon .MuiSvgIcon-root': { color: BRAND, fill: 'currentColor' },
    '&:hover': {
      borderColor: '#134F60',
      color: '#134F60',
      backgroundColor: 'rgba(13, 61, 74, 0.08)',
      '& .MuiButton-startIcon': { color: '#134F60' },
      '& .MuiButton-startIcon .MuiSvgIcon-root': { color: '#134F60', fill: 'currentColor' },
    },
    '&.Mui-disabled': {
      borderColor: 'rgba(13, 61, 74, 0.28)',
      color: 'rgba(13, 61, 74, 0.45)',
      backgroundColor: '#f5f5f5',
      '& .MuiButton-startIcon .MuiSvgIcon-root': {
        color: 'rgba(13, 61, 74, 0.45)',
        fill: 'currentColor',
      },
    },
  } as const;

  const [mediaNatural, setMediaNatural] = useState<{ w: number; h: number } | null>(null);

  const resolveLatestCoverPhoto = async (attachments: any[] | undefined) => {
    const coverCandidates = [...(attachments ?? [])]
      .filter((file: any) => {
        const fileName = String(file?.fileName ?? file?.FileName ?? '').toLowerCase();
        return fileName.startsWith('cover') &&
          (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png'));
      })
      .reverse();

    for (const file of coverCandidates) {
      const fileId = file?.id ?? file?.Id;
      if (!fileId) continue;
      try {
        const photoBlob = await EventsApi.getFileAttachment(fileId);
        return {
          id: String(fileId),
          url: URL.createObjectURL(photoBlob),
        };
      } catch (error) {
        console.warn('Skipping unavailable cover photo attachment:', fileId, error);
      }
    }

    return null;
  };

  useEffect(() => {
    const fetchCoverPhoto = async () => {
      if (pinId) {
        try {
          const pinInfo = await EventsApi.getPinFullReport(pinId);
          const coverPhoto = await resolveLatestCoverPhoto(pinInfo?.pinInfo?.fileAttachments);

          if (coverPhoto) {
            setExistingCoverPhoto(coverPhoto.url);
            setExistingCoverPhotoId(coverPhoto.id);
            setPreviewUrl(coverPhoto.url);
            setImageSrc(coverPhoto.url);
          } else {
            setExistingCoverPhoto(null);
            setExistingCoverPhotoId(null);
            setPreviewUrl(null);
            setImageSrc(null);
          }
        } catch (error) {
          console.error('Error fetching cover photo:', error);
        }
      }
    };

    if (open) {
      fetchCoverPhoto();
    }

    return () => {
      // Cleanup
      if (existingCoverPhoto) {
        URL.revokeObjectURL(existingCoverPhoto);
      }
      if (imageSrc && imageSrc !== existingCoverPhoto) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [open, pinId]);

  useEffect(() => {
    if (!showCropEditor || !cropFrameRef.current || typeof ResizeObserver === 'undefined') return;

    const element = cropFrameRef.current;
    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCropViewportSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }
    };

    updateSize();
    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    return () => observer.disconnect();
  }, [showCropEditor]);

  const createImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });
  };

  const getCroppedImg = async (
    imageSrc: string,
    cropPosition: { x: number; y: number },
    cropZoom: number
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    if (iw < 1 || ih < 1) {
      throw new Error('Geçersiz görsel boyutu');
    }

    const frameRect = cropFrameRef.current?.getBoundingClientRect();
    const frameWidth = frameRect?.width && frameRect.width > 0 ? frameRect.width : CANVAS_WIDTH;
    const frameHeight = frameRect?.height && frameRect.height > 0 ? frameRect.height : CANVAS_HEIGHT;
    const baseScale = Math.min(frameWidth / iw, frameHeight / ih);
    const safeZoom = Number.isFinite(cropZoom) && cropZoom > 0 ? cropZoom : 1;
    const renderedWidth = iw * baseScale * safeZoom;
    const renderedHeight = ih * baseScale * safeZoom;
    const safeCropX = Number.isFinite(cropPosition.x) ? cropPosition.x : 0;
    const safeCropY = Number.isFinite(cropPosition.y) ? cropPosition.y : 0;
    const offsetX = ((frameWidth - renderedWidth) / 2) + safeCropX;
    const offsetY = ((frameHeight - renderedHeight) / 2) + safeCropY;
    const scaleX = CANVAS_WIDTH / frameWidth;
    const scaleY = CANVAS_HEIGHT / frameHeight;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      offsetX * scaleX,
      offsetY * scaleY,
      renderedWidth * scaleX,
      renderedHeight * scaleY
    );

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob && blob.size > 0) {
            resolve(blob);
          } else {
            reject(new Error('Canvas to Blob conversion failed'));
          }
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          setImageSrc(result);
          setPreviewUrl(result);
          setShowCropEditor(true);
          setCropViewportSize(null);
          setCrop({ x: 0, y: 0 });
          setZoom(1);
          setMediaNatural(null);
        };
        reader.readAsDataURL(file);
        setSelectedFile(file);
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  };

  const handleSaveCrop = async () => {
    if (!imageSrc) return;

    try {
      const croppedBlob = await getCroppedImg(imageSrc, crop, zoom);
      const croppedFile = new File([croppedBlob], 'cover.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      
      // Önizleme için blob URL oluştur
      const previewBlobUrl = URL.createObjectURL(croppedBlob);
      setPreviewUrl(previewBlobUrl);
      setImageSrc(previewBlobUrl);
      setSelectedFile(croppedFile);
      setShowCropEditor(false);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !pinId || !userId) return;
    if (selectedFile.size < 1) {
      console.error('Cover upload aborted: empty file');
      alert('Kapak görseli oluşturulamadı. Lütfen yeniden kırpıp kaydedin.');
      return;
    }

    try {
      setIsUploading(true);
      const fileExtension = selectedFile.name.split('.').pop() || 'jpg';
      const newFileName = `cover${pinId}.${fileExtension}`;

      const formData = new FormData();
      formData.append('Files', selectedFile, newFileName);
      formData.append('Id', pinId);
      formData.append('UserId', userId);

      await EventsApi.updatePin(pinId, formData);
      onPhotoUploaded?.();
      onClose();
    } catch (error) {
      console.error('Error uploading cover photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = async () => {
    // Eğer mevcut bir kapak fotoğrafı varsa, backend'den sil
    if (existingCoverPhotoId) {
      try {
        setIsDeleting(true);
        await EventsApi.deleteFileAttachment(existingCoverPhotoId);
        setExistingCoverPhotoId(null);
        setExistingCoverPhoto(null);
        setPreviewUrl(null);
        setImageSrc(null);
        setSelectedFile(null);
        setShowCropEditor(false);
        onPhotoUploaded?.(); // Callback'i çağır ki liste güncellensin
      } catch (error) {
        console.error('Error deleting cover photo:', error);
        alert('Kapak fotoğrafı silinirken bir hata oluştu.');
      } finally {
        setIsDeleting(false);
      }
    } else {
      // Sadece seçili dosya varsa, sadece state'i temizle
      setSelectedFile(null);
      setPreviewUrl(null);
      setImageSrc(null);
      setShowCropEditor(false);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditPhoto = () => {
    if (previewUrl || existingCoverPhoto) {
      setImageSrc(previewUrl || existingCoverPhoto);
      setShowCropEditor(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setMediaNatural(null);
    }
  };

  const handleMediaLoaded = (media: MediaSize) => {
    setMediaNatural({
      w: media.naturalWidth,
      h: media.naturalHeight,
    });
  };

  /** Uzun / geniş fotoğrafları rapor kapağı çerçevesine yaklaşık sığdırır; ince ayar için zoom kullanın */
  const fitFrameToImage = () => {
    if (!mediaNatural || mediaNatural.h < 1) return;
    const imgRatio = mediaNatural.w / mediaNatural.h;
    let z: number;
    if (imgRatio < ASPECT_RATIO) {
      z = (imgRatio / ASPECT_RATIO) * 0.96;
    } else {
      z = (ASPECT_RATIO / imgRatio) * 0.96;
    }
    z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
    setZoom(z);
    setCrop({ x: 0, y: 0 });
  };

  const centerCrop = () => setCrop({ x: 0, y: 0 });

  const nudgeCrop = (dx: number, dy: number) => {
    setCrop((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const bumpZoom = (delta: number) => {
    setZoom((z) => {
      const next = Math.round((z + delta) * 1000) / 1000;
      return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: BRAND, fontWeight: 700 }}>
            Kapak Fotoğrafı Ekle ve Düzenle
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: BRAND, '& .MuiSvgIcon-root': { color: BRAND } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          {showCropEditor && imageSrc ? (
            <Box sx={{ width: '100%' }}>
              <Box
                ref={cropFrameRef}
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: `${COVER_PREVIEW_MAX_WIDTH_PX}px`,
                  aspectRatio: COVER_EXPORT_ASPECT_RATIO,
                  mx: 'auto',
                  bgcolor: '#000',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #C8DDE4',
                  mb: 2
                }}
              >
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={ASPECT_RATIO}
                  cropSize={cropViewportSize ?? undefined}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  zoomWithScroll
                  restrictPosition={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onMediaLoaded={handleMediaLoaded}
                  objectFit="contain"
                  cropShape="rect"
                  showGrid={false}
                />
              </Box>
              
              <Box sx={{ px: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Tooltip title="Çerçeveye sığdır (uzun fotoğraflar)">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FitScreenIcon sx={{ color: BRAND, fill: 'currentColor' }} />}
                        onClick={fitFrameToImage}
                        disabled={!mediaNatural}
                        sx={textBtnOutlinedSx}
                      >
                        Sığdır
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Görüntüyü ortala">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CenterFocusStrongIcon sx={{ color: BRAND, fill: 'currentColor' }} />}
                      onClick={centerCrop}
                      sx={textBtnOutlinedSx}
                    >
                      Ortala
                    </Button>
                  </Tooltip>
                </Box>
                <Typography variant="caption" sx={{ color: '#5A7A84', display: 'block', textAlign: 'center', mb: 1 }}>
                  Kırpma alanı rapordaki 1200x630 kapakla aynıdır. Dikey fotoğraflarda önce Sığdır’a basın; kaydırma
                  tekeri ile zoom, oklarla ince konum verin.
                </Typography>
                <Typography variant="caption" sx={{ color: '#5A7A84', display: 'block', textAlign: 'center', mb: 1 }}>
                  Çerçevenin tamamı kaydedilir; görünen siyah alanlar da çıktıya aynen dahil edilir.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                  <Tooltip title="Sola">
                    <IconButton size="small" onClick={() => nudgeCrop(CROP_NUDGE_PX, 0)} sx={iconControlSx}>
                      <ChevronLeftIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Yukarı">
                    <IconButton size="small" onClick={() => nudgeCrop(0, CROP_NUDGE_PX)} sx={iconControlSx}>
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Aşağı">
                    <IconButton size="small" onClick={() => nudgeCrop(0, -CROP_NUDGE_PX)} sx={iconControlSx}>
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sağa">
                    <IconButton size="small" onClick={() => nudgeCrop(-CROP_NUDGE_PX, 0)} sx={iconControlSx}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Tooltip title="Uzaklaştır">
                    <IconButton size="small" onClick={() => bumpZoom(-ZOOM_STEP)} sx={iconControlSx}>
                      <ZoomOutIcon />
                    </IconButton>
                  </Tooltip>
                  <Slider
                    value={zoom}
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    step={0.02}
                    onChange={(_e, value) => setZoom(value as number)}
                    sx={{
                      flex: 1,
                      color: BRAND,
                      '& .MuiSlider-rail': {
                        opacity: 1,
                        backgroundColor: 'rgba(13, 61, 74, 0.2)',
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: BRAND,
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 1px rgba(13,61,74,0.35)',
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: BRAND,
                      },
                    }}
                  />
                  <Tooltip title="Yakınlaştır">
                    <IconButton size="small" onClick={() => bumpZoom(ZOOM_STEP)} sx={iconControlSx}>
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ color: '#5A7A84', textAlign: 'center' }}>
                  Yakınlaştırma: {zoom.toFixed(2)}× (min {MIN_ZOOM} – max {MAX_ZOOM}) · Sürükleyerek de konum verebilirsiniz
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowCropEditor(false);
                    if (!selectedFile) {
                      setImageSrc(null);
                      setPreviewUrl(null);
                    }
                  }}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#b71c1c',
                      color: '#fff',
                    }
                  }}
                >
                  İptal
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveCrop}
                  startIcon={<CropIcon sx={{ color: '#fff' }} />}
                  sx={{
                    backgroundColor: BRAND,
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#134F60',
                    },
                  }}
                >
                  Kırp ve Kaydet
                </Button>
              </Box>
            </Box>
          ) : (previewUrl || existingCoverPhoto) ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: `${COVER_PREVIEW_MAX_WIDTH_PX}px`,
                  aspectRatio: COVER_EXPORT_ASPECT_RATIO,
                  position: 'relative',
                  bgcolor: '#000',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #C8DDE4',
                }}
              >
                <Box
                  component="img"
                  src={previewUrl || existingCoverPhoto || ''}
                  alt="Preview"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleEditPhoto}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)',
                      }
                    }}
                    title="Düzenle"
                  >
                    <CropIcon sx={{ color: BRAND }} />
                  </IconButton>
                  <IconButton
                    onClick={handleRemoveFile}
                    disabled={isDeleting}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)',
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(255, 255, 255, 0.6)',
                      }
                    }}
                    title="Sil"
                  >
                    <DeleteIcon sx={{ color: '#d32f2f' }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              onClick={handleClickUpload}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: `${COVER_PREVIEW_MAX_WIDTH_PX}px`,
                  aspectRatio: COVER_EXPORT_ASPECT_RATIO,
                  position: 'relative',
                  border: '2px dashed #b3c2d1',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  bgcolor: '#f3f6fa',
                  '&:hover': {
                    bgcolor: '#e8eef5'
                  }
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: BRAND, mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  Fotoğraf seçmek için tıklayın
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#d32f2f',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#b71c1c',
              color: '#fff',
            }
          }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading || showCropEditor}
          sx={{
            backgroundColor: BRAND,
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#134F60',
              color: '#fff',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#fff',
            },
          }}
        >
          {isUploading ? 'Yükleniyor...' : 'Yükle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverPhotoModal;
